import { ThunkAction, Dispatch } from 'redux-thunk';
import { AppState, getLang, getTemplateSubtype, getSelectedPackage } from '../reducers';
import * as blobApi from '../../modules/blobApi';
import { CHECKOUT_START_ENDPOINT } from '../../modules/constants';
import { handleError } from '../../modules/helpers';
import { TRANSFER } from '../reducers/domain-field';
import { generalRequestFail, invalidUserIdToken } from './errors';
import reload from '../../modules/reload';
import { Action } from '../../definitions/types';
import * as Actions from './actions-consts';
import { getEasyMarketingTrackId } from '../reducers/index';

const postwendend = require('postwendend');

export function checkoutConfigRequested(domainValue: string = ''): Action<string> {
  return {
    type: Actions.CHECKOUT_CONFIG_REQUESTED,
    payload: domainValue,
    meta: {
      tracked: { action: 'checkout-config', label: 'request' },
    },
  };
}

export function checkoutConfigGenerated(base64CheckoutConfig: string): Action<string> {
  return {
    type: Actions.CHECKOUT_CONFIG_GENERATED,
    payload: base64CheckoutConfig,
    meta: {
      tracked: { action: 'checkout-config', label: 'generate' },
    },
  };
}

export function forwardToCheckout(): ThunkAction<void, AppState, void> {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    const state = getState();
    const lang = getLang(state);

    if (state.user.idToken === '') {
      dispatch(invalidUserIdToken());
      setTimeout(reload);
      return;
    }

    const {
      activeType,
      fields: {
        product: { value: productValue, free: productFree, tld: productTld },
        transfer: { value: transferValue, free: transferFree },
      },
    } = state.domain;

    // TODO: move this to a selector function into the root reducer
    function getDomain(domainType: string): string {
      switch (domainType) {
        case TRANSFER:
          return transferValue;
        default:
          // DOMAIN_TYPE_PRODUCT
          return productValue ? productValue + productTld : undefined;
      }
    }

    // TODO: move this to a selector function into the root reducer
    function isFree(domainType: string): boolean {
      switch (domainType) {
        case TRANSFER:
          return transferFree;
        default:
          return productFree;
      }
    }

    const domainName: string = getDomain(activeType);
    const domainFree: boolean = isFree(activeType);

    dispatch(checkoutConfigRequested(domainName));

    const checkoutOptions: blobApi.CheckoutConfigBaseOptions = {
      domain: domainName,
      domainFree,
      obfuscatedSubtype: getTemplateSubtype(state),
      package: getSelectedPackage(state),
    };

    const ref = state.affiliate.ref ? { ref: state.affiliate.ref } : {};
    const easyMarketingTrackId = getEasyMarketingTrackId(state) || '';
    const trs = easyMarketingTrackId ? { easyMarketingTrackId } : {};

    const generateCheckoutConfig = domainName
      ? blobApi.generateCheckoutConfigWithDomain
      : blobApi.generateCheckoutConfigWithoutDomain;

    try {
      const response = await generateCheckoutConfig(
        { ...checkoutOptions, ...ref, ...trs },
        lang,
        state.user.idToken
      );

      if (response.status === 'error') {
        handleError(dispatch, response.errors);
        return;
      }

      dispatch(checkoutConfigGenerated(response.message));

      // tslint:disable-next-line no-any
      const payload: any = { container: response.message };

      if (window.__BB_UPSELLING__) {
        payload.checkoutConfiguration = btoa(
          JSON.stringify({ additional_products_to_offer: [4, 24, 25] })
        );
      }

      postwendend(CHECKOUT_START_ENDPOINT, payload);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn(error);
      }
      dispatch(generalRequestFail());
    }
  };
}

export function forwardingToCheckout(): Action<void> {
  return {
    type: Actions.FORWARDING_TO_CHECKOUT,
    meta: {
      tracked: { action: 'checkout', label: 'forward' },
    },
  };
}
